/**
 * Number / currency formatting (PLAN.md M7 "NUMBERS"). THB (฿) is the default currency.
 *  - COMPACT on axes:  ฿1.2M, ฿850K, ฿0
 *  - FULL in tooltips / tables: ฿1,234,567
 * Tabular figures are enforced in CSS (.num / --fnum); these helpers own the string shape.
 *
 * Currency is configurable (a future plan may carry a currency code), but THB is the default and
 * the only symbol shipped in M7. Keep these PURE and dependency-free so charts, tooltips, and the
 * ledger table all format identically.
 */

export const CURRENCY_SYMBOL = '฿';

/** Full grouped amount with the currency symbol, no decimals: `฿1,234,567`. Negative → `−฿1,234`. */
export function formatCurrency(
  value: number,
  opts?: { decimals?: number; sign?: boolean },
): string {
  const decimals = opts?.decimals ?? 0;
  const neg = value < 0;
  const abs = Math.abs(value);
  const body = abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const plus = opts?.sign && value > 0 ? '+' : '';
  return `${neg ? '−' : plus}${CURRENCY_SYMBOL}${body}`;
}

/** Compact amount for chart axes / dense labels: `฿1.2M`, `฿850K`, `฿0`, `−฿3.4M`. */
export function formatCompactCurrency(value: number): string {
  const neg = value < 0;
  const abs = Math.abs(value);
  let body: string;
  if (abs >= 1_000_000_000) body = `${trim(abs / 1_000_000_000)}B`;
  else if (abs >= 1_000_000) body = `${trim(abs / 1_000_000)}M`;
  else if (abs >= 1_000) body = `${trim(abs / 1_000)}K`;
  else body = `${Math.round(abs)}`;
  return `${neg ? '−' : ''}${CURRENCY_SYMBOL}${body}`;
}

/** A percentage from a decimal fraction: `formatPercent(0.942)` → `94.2%`. */
export function formatPercent(fraction: number, decimals = 1): string {
  return `${(fraction * 100).toFixed(decimals)}%`;
}

/** A signed delta amount for the ledger (`+฿1,200` / `−฿1,200`). */
export function formatSignedCurrency(value: number): string {
  return formatCurrency(value, { sign: true });
}

function trim(n: number): string {
  // 1 decimal place, but drop a trailing `.0` so `฿2M` not `฿2.0M`.
  return n.toFixed(1).replace(/\.0$/, '');
}
